/**
 * Custom GPT Edit Page
 * Page for editing an existing Custom GPT
 */

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Textarea } from '../../components/ui/textarea'
import { Label } from '../../components/ui/label'
import { Switch } from '../../components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { CustomGPTNavbar } from '../../components/custom-gpt/CustomGPTNavbar'
import { KnowledgeBaseUpload } from '../../components/custom-gpt/KnowledgeBaseUpload'
import { getCustomGPT, updateCustomGPT, deleteCustomGPT } from '../../lib/api/customGPT'
import { toast } from 'sonner'
import { ArrowLeft, Plus, X, Trash2 } from 'lucide-react'
import { LoadingSpinner } from '../../components/shared/LoadingSpinner'
import { API_BASE_URL } from '../../lib/config'
import type { CustomGPTFile } from '../../types/custom-gpt'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog'

export function CustomGPTEditPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instruction: '',
    is_public: false,
  })
  const [conversationStarters, setConversationStarters] = useState<string[]>([''])
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState<CustomGPTFile[]>([])

  useEffect(() => {
    if (!id) {
      navigate('/playbooks')
      return
    }

    async function loadGPT() {
      try {
        // Get current user ID
        const userResponse = await fetch(`${API_BASE_URL}api/custom-gpts/current-user`, {
          credentials: 'include'
        })
        if (!userResponse.ok) {
          toast.error('Unauthorized')
          navigate('/playbooks')
          return
        }
        const userData = await userResponse.json()
        const currentUserId = userData.user_id

        const gpt = await getCustomGPT(id)
        
        // Check ownership: if GPT is public, only owner can edit
        if (gpt.is_public === 'true' && gpt.user_id !== currentUserId) {
          toast.error('You can only edit your own public playbooks')
          navigate('/playbooks')
          return
        }

        setFormData({
          name: gpt.name,
          description: gpt.description || '',
          instruction: gpt.instruction,
          is_public: gpt.is_public === 'true',
        })
        setConversationStarters(
          gpt.conversation_starters && gpt.conversation_starters.length > 0
            ? gpt.conversation_starters
            : ['']
        )
        setKnowledgeBaseFiles(gpt.files || [])
      } catch (error) {
        toast.error('Failed to load playbook')
        navigate('/playbooks')
      } finally {
        setLoading(false)
      }
    }

    loadGPT()
  }, [id, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!id) return

    // Validation
    if (!formData.name.trim()) {
      toast.error('Name is required')
      return
    }
    if (!formData.instruction.trim()) {
      toast.error('Instruction is required')
      return
    }

    setSaving(true)
    try {
      const filteredStarters = conversationStarters.filter(s => s.trim())
      await updateCustomGPT(id, {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        instruction: formData.instruction.trim(),
        is_public: formData.is_public,
        conversation_starters: filteredStarters.length > 0 ? filteredStarters : undefined,
      })
      
      toast.success('Playbook updated successfully!')
      navigate('/playbooks')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update playbook')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return

    try {
      await deleteCustomGPT(id)
      toast.success('Playbook deleted successfully!')
      navigate('/playbooks')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete playbook')
    }
  }

  const addConversationStarter = () => {
    if (conversationStarters.length < 10) {
      setConversationStarters([...conversationStarters, ''])
    }
  }

  const removeConversationStarter = (index: number) => {
    setConversationStarters(conversationStarters.filter((_, i) => i !== index))
  }

  const updateConversationStarter = (index: number, value: string) => {
    if (value.length <= 200) {
      const updated = [...conversationStarters]
      updated[index] = value
      setConversationStarters(updated)
    }
  }

  if (loading) {
    return (
      <>
        <CustomGPTNavbar />
        <div className="min-h-screen bg-light-surface dark:bg-slate-900 flex items-center justify-center">
          <LoadingSpinner size="lg" message="Loading playbook..." />
        </div>
      </>
    )
  }

  return (
    <>
      <CustomGPTNavbar />
      <div className="min-h-screen bg-light-surface dark:bg-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/playbooks')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Playbooks
          </Button>

        <Card>
          <CardHeader>
            <CardTitle>Edit Playbook</CardTitle>
            <CardDescription>
              Update your playbook settings and instructions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="My Playbook"
                  maxLength={100}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {formData.name.length}/100 characters
                </p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="A brief description of your playbook"
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground">
                  {formData.description.length}/500 characters
                </p>
              </div>

              {/* Instruction */}
              <div className="space-y-2">
                <Label htmlFor="instruction">Instruction *</Label>
                <Textarea
                  id="instruction"
                  value={formData.instruction}
                  onChange={(e) => setFormData({ ...formData, instruction: e.target.value })}
                  placeholder="You are a helpful assistant that..."
                  rows={10}
                  maxLength={15000}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {formData.instruction.length}/15000 characters
                </p>
              </div>

              {/* Public/Private */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="is_public">Make this playbook public</Label>
                  <p className="text-xs text-muted-foreground">
                    Other users will be able to see and use this playbook
                  </p>
                </div>
                <Switch
                  id="is_public"
                  checked={formData.is_public}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                />
              </div>

              {/* Conversation Starters */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Conversation Starters</Label>
                  {conversationStarters.length < 10 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addConversationStarter}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Starter
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Add up to 10 conversation starters (max 200 characters each)
                </p>
                <div className="space-y-2">
                  {conversationStarters.map((starter, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={starter}
                        onChange={(e) => updateConversationStarter(index, e.target.value)}
                        placeholder={`Conversation starter ${index + 1}`}
                        maxLength={200}
                      />
                      {conversationStarters.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeConversationStarter(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Knowledge Base Files */}
              <div className="space-y-2">
                <KnowledgeBaseUpload
                  gptId={id || null}
                  existingFiles={knowledgeBaseFiles}
                  disabled={saving}
                  onFilesChange={(files) => setKnowledgeBaseFiles(files)}
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4 justify-between">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="destructive" disabled={saving}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Playbook
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete your playbook.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-destructive">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/playbooks')}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
        </div>
      </div>
    </>
  )
}

